export const hospitalInfo = {
  name: "NANGUBA HOSPITAL",
  shortName: "Nanguba Hospital",
  tagline: "Classic professionalism. Modern patient care.",
  phoneDisplay: "+255 619 586 628",
  phoneLink: "tel:+255619586628",
  email: "info@nangubahealth.com",
  emailLink: "mailto:info@nangubahealth.com",
  websiteLabel: "nanguba.com",
  websiteUrl: "https://nangubahealth.com",
  scheduleUrl: "https://nangubahealth.com/schedule",
  staffUrl: "https://nangubahealth.com/staff",
};

export const departments = [
  {
    subItem: "aggregates",
    icon: "consultation",
    badge: "Outpatient Care",
    title: "Outpatient & General Consultation",
    description:
      "Professional assessment, routine checkups, follow-up visits, and referral guidance for everyday health needs.",
    teaser:
      "A calm first point of contact for everyday health concerns and ongoing wellness support.",
    detailLabel: "Outpatient Care",
    detailSubtitle:
      "Consultation-led care for first assessments, routine reviews, preventive visits, and follow-up planning.",
    detailOverview:
      "Our outpatient team focuses on careful listening, clear clinical guidance, and coordinated next steps so each visit feels organized, respectful, and efficient.",
    highlights: [
      "Routine consultations for new and returning patients.",
      "Structured follow-up for ongoing care plans and treatment review.",
      "Referral coordination when laboratory, pharmacy, or further clinical support is needed.",
    ],
    metrics: [
      { value: "Clear Plans", label: "Every visit is paired with understandable next steps and care guidance." },
      { value: "Coordinated", label: "Consultation, referrals, and follow-up are handled as one care pathway." },
      { value: "Patient-First", label: "Appointments are designed to balance professionalism, comfort, and time." },
    ],
    carePoints: [
      "Welcoming registration and appointment support.",
      "Clinical assessment with attention to symptoms, history, and current needs.",
      "Practical treatment advice, prescriptions, or referrals before you leave.",
    ],
    accent: "#157c88",
  },
  {
    subItem: "crushing",
    icon: "maternity",
    badge: "Women's Health",
    title: "Maternal & Women's Health",
    description:
      "Supportive care for women's health, antenatal follow-up, maternal monitoring, and guidance through important life stages.",
    teaser:
      "Warm, respectful support for women before, during, and after major care milestones.",
    detailLabel: "Women's Health",
    detailSubtitle:
      "Patient-centered care for maternal health, routine women's consultations, and structured follow-up.",
    detailOverview:
      "This department is designed to provide privacy, reassurance, and continuity so women can receive clinical attention in an environment that feels calm and dignified.",
    highlights: [
      "Antenatal monitoring and maternal check-ins with thoughtful follow-up.",
      "Routine women's consultations supported by clear clinical communication.",
      "Referral guidance and ongoing care coordination when further support is needed.",
    ],
    metrics: [
      { value: "Respectful", label: "Care is delivered with privacy, dignity, and clear communication." },
      { value: "Continuous", label: "Follow-up is structured to support every stage of care." },
      { value: "Reassuring", label: "Patients receive guidance that helps them feel informed and supported." },
    ],
    carePoints: [
      "Appointment planning that makes each visit feel organized and prepared.",
      "Routine review, monitoring, and clinician guidance based on current needs.",
      "Next-step planning for follow-up, diagnostics, or continued maternal support.",
    ],
    accent: "#b86d7b",
  },
  {
    subItem: "haulage",
    icon: "pediatrics",
    badge: "Child Health",
    title: "Pediatrics & Family Care",
    description:
      "Compassionate pediatric assessment, child wellness follow-up, and family-centered guidance for growing children.",
    teaser:
      "Care that supports children with warmth while helping parents make informed decisions.",
    detailLabel: "Pediatrics",
    detailSubtitle:
      "Gentle, family-aware care for children, wellness reviews, and common childhood concerns.",
    detailOverview:
      "We create a reassuring experience for both children and caregivers by combining attentive assessment with clear explanations and practical next steps.",
    highlights: [
      "Child wellness reviews and age-appropriate clinical assessment.",
      "Family-centered communication that helps caregivers understand each care plan.",
      "Follow-up support for ongoing monitoring, medication review, or referral needs.",
    ],
    metrics: [
      { value: "Family-Centered", label: "Parents and caregivers stay informed throughout the care process." },
      { value: "Gentle", label: "Visits are designed to feel calm, warm, and supportive for children." },
      { value: "Practical", label: "Advice focuses on everyday care, recovery, and when to return." },
    ],
    carePoints: [
      "Child-friendly evaluation focused on symptoms, comfort, and medical history.",
      "Clear clinician guidance for caregivers during and after the consultation.",
      "Follow-up planning that supports recovery, wellness, and confidence at home.",
    ],
    accent: "#3c6fb0",
  },
  {
    subItem: "coal",
    icon: "diagnostics",
    badge: "Diagnostics",
    title: "Diagnostics & Laboratory",
    description:
      "Timely laboratory and diagnostic support that helps clinicians confirm findings and plan treatment with confidence.",
    teaser:
      "Testing and clinical support that make decision-making clearer and more efficient.",
    detailLabel: "Diagnostics",
    detailSubtitle:
      "Dependable diagnostic support for clearer assessment, treatment planning, and ongoing monitoring.",
    detailOverview:
      "Our diagnostic workflow is built to strengthen clinical decision-making by supporting accurate assessment, organized reporting, and coordinated follow-up.",
    highlights: [
      "Sample collection and diagnostic support guided by clinical need.",
      "Organized reporting that helps care teams plan the next stage of treatment.",
      "Coordination with treating clinicians for a smoother patient journey.",
    ],
    metrics: [
      { value: "Timely", label: "Diagnostic support is structured to keep care moving without confusion." },
      { value: "Informed", label: "Results contribute to clearer clinical assessment and treatment planning." },
      { value: "Connected", label: "Testing and consultation are tied together through coordinated follow-up." },
    ],
    carePoints: [
      "Referral review and preparation before the testing process begins.",
      "Organized sample handling and diagnostic support aligned to the care plan.",
      "Results fed back into the next consultation, treatment, or follow-up discussion.",
    ],
    accent: "#4d5d8c",
  },
  {
    subItem: "flatdeck",
    icon: "pharmacy",
    badge: "Pharmacy",
    title: "Pharmacy & Medication Counseling",
    description:
      "Prescription support, medication dispensing, and practical counseling that helps patients use treatment safely and confidently.",
    teaser:
      "Clear medication guidance to support treatment plans and everyday adherence.",
    detailLabel: "Pharmacy",
    detailSubtitle:
      "Prescription fulfillment and medication counseling designed to support safe, confident treatment use.",
    detailOverview:
      "Our pharmacy support focuses on clarity, safety, and patient understanding so medicines are dispensed with the guidance needed to use them well.",
    highlights: [
      "Prescription dispensing with practical support on dosage and timing.",
      "Medication counseling that improves confidence and understanding.",
      "Coordination with clinicians when treatment changes or follow-up is required.",
    ],
    metrics: [
      { value: "Safe Use", label: "Patients receive guidance that supports careful, informed medication use." },
      { value: "Clear Advice", label: "Instructions are communicated in a practical and understandable way." },
      { value: "Treatment Support", label: "Pharmacy care strengthens the full treatment journey, not only dispensing." },
    ],
    carePoints: [
      "Prescription review before medication is dispensed.",
      "Counseling on dosage, timing, and safe everyday use.",
      "Advice on follow-up, refills, and when to return for further review.",
    ],
    accent: "#4f8b5f",
  },
  {
    subItem: "tipping",
    icon: "inpatient",
    badge: "Inpatient Care",
    title: "Inpatient & Recovery Support",
    description:
      "Organized ward-based support, observation, and recovery planning for patients who need continued monitoring and nursing care.",
    teaser:
      "Structured inpatient support centered on comfort, monitoring, and well-managed recovery.",
    detailLabel: "Inpatient Care",
    detailSubtitle:
      "Supportive inpatient care for observation, recovery, and steady coordination between clinical teams and families.",
    detailOverview:
      "Where a patient needs continued monitoring or a longer stay, our inpatient approach emphasizes comfort, communication, and coordinated recovery planning.",
    highlights: [
      "Observation and nursing support for patients who require continued monitoring.",
      "Treatment coordination between clinicians, caregivers, and support teams.",
      "Discharge guidance with clear follow-up steps for recovery beyond the ward.",
    ],
    metrics: [
      { value: "Calm Spaces", label: "Recovery is supported by an orderly, patient-focused environment." },
      { value: "Continuous", label: "Monitoring and clinical coordination continue throughout the stay." },
      { value: "Recovery-Focused", label: "Discharge planning supports confidence and continuity after care." },
    ],
    carePoints: [
      "Admission and orientation so patients and families understand the care process.",
      "Bedside monitoring with attention to comfort, progress, and communication.",
      "Discharge planning with practical instructions for recovery and follow-up.",
    ],
    accent: "#2f6b73",
  },
];

export const featuredDepartments = departments.filter((department) =>
  ["aggregates", "crushing", "haulage", "coal"].includes(department.subItem)
);

export const carePillars = [
  {
    icon: "safety",
    title: "Patient Safety First",
    text: "Clinical decisions, hygiene, and day-to-day operations are guided by safe, careful practice.",
    accent: "#157c88",
  },
  {
    icon: "team",
    title: "Professional Care Teams",
    text: "Patients are supported by staff who value competence, empathy, and clear communication.",
    accent: "#1b4265",
  },
  {
    icon: "calm",
    title: "Clean & Calm Spaces",
    text: "We believe a reassuring environment helps patients feel respected, comfortable, and cared for.",
    accent: "#4d5d8c",
  },
  {
    icon: "followup",
    title: "Coordinated Follow-Up",
    text: "Consultation, diagnostics, pharmacy, and recovery support are connected to keep care moving well.",
    accent: "#4f8b5f",
  },
];

export const quickAccessLinks = [
  {
    icon: "calendar",
    title: "Schedule Appointment",
    description: "Book or request a visit online through the hospital scheduler.",
    href: hospitalInfo.scheduleUrl,
    label: "Open Scheduler",
  },
  {
    icon: "staff",
    title: "Staff Login",
    description: "Secure access for internal teams and authorized hospital staff.",
    href: hospitalInfo.staffUrl,
    label: "Open Staff Portal",
  },
  {
    icon: "phone",
    title: "Call Reception",
    description: "Speak with the hospital team for appointments, guidance, or general inquiries.",
    href: hospitalInfo.phoneLink,
    label: hospitalInfo.phoneDisplay,
  },
  {
    icon: "email",
    title: "Email Support",
    description: "Send non-urgent questions or coordination requests to our team.",
    href: hospitalInfo.emailLink,
    label: hospitalInfo.email,
  },
];

export const aboutValues = [
  {
    title: "Our Mission",
    text: "To provide accessible, professional, and compassionate hospital care that supports patients and families with dignity.",
  },
  {
    title: "Our Vision",
    text: "To be a trusted hospital known for reliable service, clean facilities, and a patient-centered experience.",
  },
  {
    title: "Our Approach",
    text: "We combine modern clinical workflows with respectful communication so patients can move from consultation to treatment with confidence.",
  },
];

export const aboutHighlights = [
  "Patient-centered consultations and follow-up planning.",
  "Modern service delivery with a classic, professional atmosphere.",
  "Coordinated departments designed to keep care clear and efficient.",
  "Accessible digital touchpoints for scheduling and staff operations.",
];

export function getDepartmentBySubItem(subItem) {
  return departments.find((department) => department.subItem === subItem);
}
